import { logger } from './utils/logger';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
if (!API_BASE_URL && process.env.NODE_ENV === 'production') {
  logger.error('REACT_APP_API_BASE_URL is not set');
}

// ─── Error extraction ──────────────────────────────────────────────────────────
/**
 * Converts any caught error into a clean, user-friendly string.
 *
 * Priority order:
 *  1. First field-level validation error  (errors.field[0])
 *  2. Top-level message from the API body (json.message)
 *  3. Generic HTTP status fallback
 *  4. Network / unknown error
 *
 * Never exposes raw status codes, JSON dumps, or technical details to the user.
 */
export function extractError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;

  // If it's already a plain string, return it directly
  if (typeof err === 'string') return err || fallback;

  const e = err as { apiBody?: { errors?: Record<string, string[]>; message?: string }; message?: string };

  // First try the structured API error payload stored on the error
  if (e?.apiBody) {
    const body = e.apiBody;
    // Field errors: errors.field[0]
    if (body?.errors && typeof body.errors === 'object') {
      const firstField = Object.values(body.errors).flat()[0];
      if (typeof firstField === 'string' && firstField.trim()) return firstField;
    }
    // Top-level message
    if (typeof body?.message === 'string' && body.message.trim()) return body.message;
  }

  // Fall back to the error's message property — but strip any JSON/status-code content
  if (typeof e?.message === 'string' && e.message.trim()) {
    const msg = e.message;
    // If the message contains JSON or "API error:" prefix, extract just the human part
    if (msg.includes('API error:') || msg.includes('"success"') || msg.includes('{')) {
      // Try to pull json.message out of the embedded payload
      try {
        const jsonStart = msg.indexOf('{');
        if (jsonStart !== -1) {
          const json = JSON.parse(msg.substring(jsonStart));
          if (json?.errors) {
            const first = Object.values(json.errors as Record<string, string[]>).flat()[0];
            if (typeof first === 'string' && first.trim()) return first;
          }
          if (typeof json?.message === 'string' && json.message.trim()) return json.message;
        }
      } catch { /* ignore */ }
      return fallback;
    }
    return msg;
  }

  return fallback;
}

// ─── Internal class that carries the parsed API body ─────────────────────────
class ApiError extends Error {
  apiBody: unknown;
  status: number;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.apiBody = body;
  }
}

// ─── Response handler ──────────────────────────────────────────────────────────
async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // 401 with an existing token means the session has expired.
    // Clear stored auth data and redirect to the login page so the
    // user is not silently left in a broken authenticated state.
    if (res.status === 401) {
      // Only treat a 401 as a session expiry when the user actually had a session.
      // Without this guard, public pages that call auth-required endpoints (e.g. /agents)
      // would redirect unauthenticated visitors to /login.
      const hadSession = !!localStorage.getItem('srs_user');
      // Clear any legacy localStorage tokens
      localStorage.removeItem('srs_token');
      localStorage.removeItem('srs_user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      // The HTTP-only cookie is cleared by the backend on logout
      if (hadSession) {
        window.dispatchEvent(new CustomEvent('srs:session-expired'));
        return new Promise(() => {});
      }
      // No prior session — let the call fail normally so the page can handle it gracefully
      throw new ApiError('Unauthorised', 401);
    }

    const text = await res.text().catch(() => '');

    // Maintenance mode: the API gates non-admin traffic with a 503 + { maintenance: true }.
    // Surface it as a global event so the app can show a full-page maintenance screen,
    // then halt this request (like the 401 session-expiry path) so callers don't error out.
    if (res.status === 503) {
      try {
        const body = JSON.parse(text);
        if (body?.maintenance) {
          window.dispatchEvent(new CustomEvent('srs:maintenance', {
            detail: { message: typeof body.message === 'string' ? body.message : undefined },
          }));
          return new Promise(() => {});
        }
      } catch { /* not a maintenance payload — fall through to normal handling */ }
    }

    // HTML error page (e.g. 500 from server misconfiguration)
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      throw new ApiError('The server encountered an error. Please try again later.', res.status);
    }

    // Try to parse structured JSON error from API
    try {
      const json = JSON.parse(text);

      // Build a clean message: prefer first field error, then top-level message
      let cleanMessage = 'Something went wrong. Please try again.';
      if (json?.errors && typeof json.errors === 'object') {
        const firstField = Object.values(json.errors as Record<string, string[]>).flat()[0];
        if (typeof firstField === 'string' && firstField.trim()) {
          cleanMessage = firstField;
        }
      } else if (typeof json?.message === 'string' && json.message.trim()) {
        cleanMessage = json.message;
      } else if (typeof json?.error === 'string' && json.error.trim()) {
        cleanMessage = json.error;
      }

      // Map common HTTP statuses to friendly fallbacks if message is still generic
      if (cleanMessage === 'Something went wrong. Please try again.') {
        cleanMessage = httpStatusMessage(res.status);
      }

      throw new ApiError(cleanMessage, res.status, json);
    } catch (parseErr) {
      // parseErr could be the ApiError we just threw — re-throw it
      if (parseErr instanceof ApiError) throw parseErr;
      // Otherwise it's a JSON parse failure
      throw new ApiError(httpStatusMessage(res.status), res.status);
    }
  }

  if (res.status === 204) return {} as T;

  try {
    const text = await res.text();
    if (!text) return {} as T;
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError('Received an unexpected response format. Please try again.', 200);
  }
}

/** Maps HTTP status codes to short, user-friendly sentences. */
function httpStatusMessage(status: number): string {
  switch (status) {
    case 400: return 'The request was invalid. Please check your input.';
    case 401: return 'You are not logged in. Please sign in and try again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'The requested resource was not found.';
    case 408: return 'The request timed out. Please check your connection and try again.';
    case 409: return 'This action conflicts with existing data. Please refresh and try again.';
    case 422: return 'Some of the information provided is invalid. Please check and try again.';
    case 429: return 'Too many attempts. Please wait a moment and try again.';
    case 500: return 'The server encountered an error. Please try again later.';
    case 502: return 'Service temporarily unavailable. Please try again in a moment.';
    case 503: return 'Service is currently unavailable. Please try again later.';
    default:  return 'Something went wrong. Please try again.';
  }
}

// ─── Silent token refresh ───────────────────────────────────────────────────────
// On a 401 for an authenticated user, transparently call /auth/refresh once and
// retry the original request. Concurrent 401s share a single in-flight refresh so
// we never fire multiple refreshes at once. Login/refresh endpoints are excluded
// (a 401 there is a real auth failure, not an expired session).
let refreshInFlight: Promise<boolean> | null = null;

function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((r) => r.ok)
      .catch(() => false);
    // Allow a fresh refresh on the next expiry once this one settles.
    refreshInFlight.finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

const NO_REFRESH_PATHS = ['/auth/refresh', '/auth/login', '/auth/register'];

async function send<T>(path: string, init: RequestInit): Promise<T> {
  let res = await fetch(`${API_BASE_URL}${path}`, init);

  if (
    res.status === 401 &&
    !NO_REFRESH_PATHS.some((p) => path.startsWith(p)) &&
    !!localStorage.getItem('srs_user')
  ) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      // Retry the original request exactly once with the renewed cookie.
      res = await fetch(`${API_BASE_URL}${path}`, init);
    }
  }

  return handleResponse<T>(res);
}

// ─── Public API functions ──────────────────────────────────────────────────────

// Auth is handled exclusively via the HTTP-only `srs_token` cookie set by the backend.
// The Authorization header is no longer sent from the client — JwtFromCookieMiddleware
// on the server injects it from the cookie before the auth guard runs.
function baseHeaders(extra?: HeadersInit): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...(extra ?? {}),
  };
}

export async function apiGet<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  return send<T>(path, {
    ...options,
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
  });
}

export async function apiPost<T = unknown, B = unknown>(
  path: string,
  body: B,
  options?: RequestInit,
): Promise<T> {
  return send<T>(path, {
    ...options,
    method: options?.method ?? 'POST',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
    body: JSON.stringify(body),
  });
}

export async function apiPut<T = unknown, B = unknown>(
  path: string,
  body: B,
  options?: RequestInit,
): Promise<T> {
  return send<T>(path, {
    ...options,
    method: 'PUT',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
    body: JSON.stringify(body),
  });
}

export async function apiPostFormData<T = unknown>(path: string, formData: FormData): Promise<T> {
  return send<T>(path, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    // No Content-Type header — browser sets multipart/form-data with boundary automatically
  });
}

export async function apiDelete<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  return send<T>(path, {
    ...options,
    method: 'DELETE',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
  });
}
